
import React from 'react';
import SectionLayout from '@/components/SectionLayout';
import { useBreakpoint } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Brain, Clock, Target, Zap, AlertCircle, CheckCircle2, TrendingUp, Focus, Lightbulb, Users, DollarSign } from 'lucide-react';

const ObjectionHandlingSection = () => {
  const { isMobile } = useBreakpoint();

  const realityCheck = {
    timeToCreate: "65+ hours",
    hourlyValue: "$150/hour",
    totalCost: "$9,750+",
    iterations: "3-5 revisions"
  };

  const distractionTasks = [
    {
      icon: AlertCircle,
      title: "Design & Templates",
      time: "20-40 hours",
      description: "Learning design principles, color theory, typography",
      impact: "Takes focus away from client work"
    },
    {
      icon: AlertCircle,
      title: "Copywriting",
      time: "15-30 hours", 
      description: "Crafting compelling headlines, sales copy, CTAs",
      impact: "Your voice gets lost in generic templates"
    },
    {
      icon: AlertCircle,
      title: "Technical Setup",
      time: "10-25 hours",
      description: "Tools, integrations, hosting configurations",
      impact: "Time away from your expertise"
    }
  ];

  const zoneOfGeniusActivities = [
    {
      icon: Users,
      title: "Building Relationships",
      description: "Personal connections that close deals",
      value: "Irreplaceable human touch"
    },
    {
      icon: Brain,
      title: "Strategic Consulting",
      description: "Using your expertise to solve problems",
      value: "Your unique knowledge premium"
    },
    {
      icon: TrendingUp,
      title: "Business Growth",
      description: "Scaling, partnerships, strategic decisions",
      value: "Activities that multiply income"
    },
    {
      icon: Lightbulb,
      title: "Innovation",
      description: "Creating new solutions and services",
      value: "Competitive differentiation"
    }
  ];

  return (
    <SectionLayout 
      backgroundColor="bg-white" 
      paddingY="py-20 md:py-28"
      containerWidth="wide"
    >
      <div className="max-w-6xl mx-auto space-y-16">
        
        {/* Header */}
        <div className="text-center space-y-6">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-[#003a70] rounded-full shadow-lg">
            <Brain className="text-[#FEE6D8]" size={24} />
            <span className="text-white font-semibold text-lg">Reality Check</span>
          </div>
          
          <h2 className={cn(
            "font-bold text-gray-900 leading-tight max-w-4xl mx-auto",
            isMobile ? "text-3xl" : "text-4xl lg:text-5xl"
          )}>
            "I Can Do This Myself"
            <span className="block text-[#003a70] mt-2">
              But Should You?
            </span>
          </h2>
        </div>

        {/* Reality Check Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
            <div className="text-2xl md:text-3xl font-bold text-[#003a70] mb-2">
              {realityCheck.timeToCreate}
            </div>
            <p className="text-gray-600 text-sm font-medium">Total Time Needed</p>
          </div>
          
          <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
            <div className="text-2xl md:text-3xl font-bold text-[#003a70] mb-2">
              {realityCheck.hourlyValue}
            </div>
            <p className="text-gray-600 text-sm font-medium">Your Time Value</p>
          </div>
          
          <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
            <div className="text-2xl md:text-3xl font-bold text-red-600 mb-2">
              {realityCheck.totalCost}
            </div>
            <p className="text-gray-600 text-sm font-medium">Opportunity Cost</p>
          </div>
          
          <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
            <div className="text-2xl md:text-3xl font-bold text-[#003a70] mb-2">
              {realityCheck.iterations}
            </div>
            <p className="text-gray-600 text-sm font-medium">Typical Revisions</p>
          </div>
        </div>

        {/* Two Column Comparison */}
        <div className="grid md:grid-cols-2 gap-8">
          
          {/* DIY Costs */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center border-2 border-red-200">
                <Clock className="text-red-600" size={24} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">DIY Reality</h3>
            </div>
            
            <div className="space-y-4">
              {distractionTasks.map((task, index) => (
                <div key={index} className="bg-red-50 border-2 border-red-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0 border border-red-200">
                      <task.icon className="text-red-600" size={20} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-gray-900">{task.title}</h4>
                        <span className="text-red-600 text-sm font-bold bg-red-100 px-2 py-1 rounded">{task.time}</span>
                      </div>
                      <p className="text-gray-700 text-sm mb-2">{task.description}</p>
                      <p className="text-red-700 text-xs font-medium bg-red-100 px-2 py-1 rounded inline-block">{task.impact}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Zone of Genius */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#FEE6D8] rounded-lg flex items-center justify-center border-2 border-[#FEE6D8]">
                <Target className="text-[#003a70]" size={24} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Your Zone of Genius</h3>
            </div>
            
            <div className="space-y-4">
              {zoneOfGeniusActivities.map((activity, index) => (
                <div key={index} className="bg-[#FEE6D8]/30 border-2 border-[#FEE6D8] rounded-xl p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#FEE6D8] rounded-lg flex items-center justify-center flex-shrink-0 border border-orange-200">
                      <activity.icon className="text-[#003a70]" size={20} />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-2">{activity.title}</h4>
                      <p className="text-gray-700 text-sm mb-2">{activity.description}</p>
                      <p className="text-[#003a70] text-xs font-medium bg-[#FEE6D8] px-2 py-1 rounded inline-block">{activity.value}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* The Math */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-8 border-2 border-gray-200 shadow-lg">
          <div className="text-center space-y-6">
            <div className="flex items-center justify-center gap-3">
              <div className="w-12 h-12 bg-[#003a70] rounded-full flex items-center justify-center">
                <DollarSign className="text-white" size={24} />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900">The Math is Simple</h3>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 text-left">
              {/* Wrong Choice */}
              <div className="space-y-4">
                <h4 className="text-xl font-bold text-red-600 flex items-center gap-2">
                  <AlertCircle size={20} />
                  DIY Approach
                </h4>
                <div className="space-y-2 text-gray-700">
                  <p>• Spend 65+ hours learning design/copy</p>
                  <p>• Miss $9,750+ in billable work</p>
                  <p>• Create amateur-looking materials</p>
                  <p>• Still lack professional positioning</p>
                </div>
                <div className="bg-red-100 rounded-lg p-4 border-2 border-red-200">
                  <p className="text-red-700 font-bold">Total Loss: $9,750+ in opportunity cost</p>
                </div>
              </div>

              {/* Smart Choice */}
              <div className="space-y-4">
                <h4 className="text-xl font-bold text-green-600 flex items-center gap-2">
                  <CheckCircle2 size={20} />
                  Smart Approach
                </h4>
                <div className="space-y-2 text-gray-700">
                  <p>• Get professional materials instantly</p>
                  <p>• Use saved time for client work</p>
                  <p>• Earn $9,750+ in additional revenue</p>
                  <p>• Project expert credibility immediately</p>
                </div>
                <div className="bg-green-100 rounded-lg p-4 border-2 border-green-200">
                  <p className="text-green-700 font-bold">Net Gain: $9,750+ in additional income</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Final Statement */}
        <div className="text-center bg-[#003a70] rounded-2xl p-8 border-2 border-[#003a70] shadow-xl">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-12 bg-[#FEE6D8] rounded-full flex items-center justify-center">
              <Focus className="text-[#003a70]" size={24} />
            </div>
          </div>
          
          <h3 className={cn(
            "font-bold text-white mb-4",
            isMobile ? "text-xl" : "text-2xl lg:text-3xl"
          )}>
            Stay in Your Zone of Genius
          </h3>
          
          <p className={cn(
            "text-white/90 leading-relaxed",
            isMobile ? "text-lg" : "text-xl"
          )}>
            The question isn't whether you <em>can</em> do it yourself.
            <br />
            The question is whether you <span className="text-[#FEE6D8] font-semibold">should</span>.
          </p>
        </div>

      </div>
    </SectionLayout>
  );
};

export default ObjectionHandlingSection;
